<template>
    <div class="bulk-action-bar">
        <div class="bulk-count">
            <i class="fas fa-check-square mr-2"></i>
            <span class="bulk-count-number">{{ count }}</span>
            <span class="bulk-count-label">selected</span>
        </div>
        <div class="bulk-divider"></div>
        <div class="bulk-actions">
            <template v-if="mode === 'trash'">
                <button class="button is-small is-rounded is-success" @click="$emit('restore')" :disabled="working">
                    <i class="fad fa-rotate-left mr-2"></i>Restore
                </button>
            </template>
            <template v-else>
                <b-dropdown
                    aria-role="menu"
                    position="is-top-right"
                    append-to-body
                    scrollable
                    max-height="360"
                    trap-focus
                    :disabled="working"
                >
                    <template #trigger>
                        <button class="button is-small is-rounded is-dark">
                            <i class="fad fa-folder-arrow-up mr-2"></i>Move to Folder
                            <i class="fas fa-caret-up ml-2"></i>
                        </button>
                    </template>
                    <div class="folder-search" v-if="folders.length > 8" @click.stop>
                        <b-input
                            v-model="folderFilter"
                            size="is-small"
                            placeholder="Filter folders..."
                            icon="search"
                            @click.native.stop
                        />
                    </div>
                    <b-dropdown-item @click="$emit('move', null)">
                        <i class="fad fa-fw fa-folder-xmark mr-2"></i>Unsorted (no folder)
                    </b-dropdown-item>
                    <hr class="dropdown-divider" v-if="filteredFolders.length">
                    <b-dropdown-item v-for="f in filteredFolders" :key="f.id" @click="$emit('move', f.id)">
                        <i class="fad fa-fw fa-folder mr-2"></i>{{ f.title }}
                    </b-dropdown-item>
                    <b-dropdown-item v-if="!folders.length" disabled class="is-italic">
                        No folders available
                    </b-dropdown-item>
                    <b-dropdown-item v-else-if="!filteredFolders.length" disabled class="is-italic">
                        No folders match "{{ folderFilter }}"
                    </b-dropdown-item>
                </b-dropdown>
                <button class="button is-small is-rounded is-info" @click="$emit('download')" :disabled="working">
                    <i class="fad mr-2" :class="downloading ? 'fa-spinner fa-spin-pulse' : 'fa-download'"></i>
                    {{ downloading ? 'Preparing…' : 'Download' }}
                </button>
                <button class="button is-small is-rounded is-danger" @click="$emit('delete')" :disabled="working">
                    <i class="fad fa-trash mr-2"></i>Delete
                </button>
            </template>
            <button class="button is-small is-rounded is-dark is-outlined" @click="$emit('clear')" :disabled="working">
                Clear
            </button>
        </div>
    </div>
</template>

<script>
    export default {
        props: {
            count: { type: Number, required: true },
            folders: { type: Array, default: () => [] },
            working: { type: Boolean, default: false },
            downloading: { type: Boolean, default: false },
            mode: { type: String, default: 'active' } // 'active' | 'trash'
        },
        data() {
            return { folderFilter: '' };
        },
        computed: {
            filteredFolders() {
                const q = (this.folderFilter || '').trim().toLowerCase();
                if (!q) return this.folders;
                return this.folders.filter(f => (f.title || '').toLowerCase().includes(q));
            }
        }
    };
</script>

<style lang="scss" scoped>
    .bulk-action-bar {
        position: absolute;
        bottom: 1rem;
        left: 50%;
        transform: translateX(-50%);
        z-index: 20;
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.45rem 0.65rem 0.45rem 1rem;
        background: #2c3e50;
        color: #ffffff;
        border-radius: 999px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
        font-size: 0.9rem;
    }

    .bulk-count {
        color: #ffffff !important;
        white-space: nowrap;
        display: flex;
        align-items: baseline;
        gap: 0.35rem;

        i {
            color: #88c5ff;
        }
    }

    .bulk-count-number {
        color: #ffffff !important;
        font-weight: 700;
        font-size: 1rem;
    }

    .bulk-count-label {
        color: #d4dae2 !important;
    }

    .bulk-divider {
        width: 1px;
        align-self: stretch;
        background: rgba(255, 255, 255, 0.18);
    }

    .bulk-actions {
        display: flex;
        gap: 0.35rem;

        .button.is-dark.is-outlined {
            background: transparent;
            border-color: rgba(255, 255, 255, 0.35);
            color: #ffffff;

            &:hover {
                background: rgba(255, 255, 255, 0.12);
                border-color: rgba(255, 255, 255, 0.55);
                color: #ffffff;
            }
        }
    }
</style>

<style lang="scss">
    // sticky filter input at the top of the bulk move dropdown; not scoped so
    // it applies through append-to-body where scoped styles don't reach.
    // The negative top margin eats .dropdown-content's default top padding so
    // items can't peek through above the sticky filter; the extra top padding
    // inside the element restores the visual spacing above the input.
    .dropdown-menu .folder-search {
        position: sticky;
        top: 0;
        background: white;
        padding: 1rem 0.75rem 0.4rem;
        margin-top: -0.5rem;
        border-bottom: 1px solid #f0f0f0;
        z-index: 1;
    }
</style>
